import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { RELEASE_INFO } from "@/generated/releaseInfo";
import { getReleaseSyncState } from "@/lib/releaseStatus";
import { useAuth } from "@/context/AuthContext";

const TONE_CLASSES = {
  synced: "border-emerald-200/80 bg-emerald-50/85 text-emerald-700",
  behind: "border-amber-300/80 bg-amber-50/90 text-amber-800",
  ahead: "border-sky-300/80 bg-sky-50/90 text-sky-800",
  checking: "border-slate-200/70 bg-slate-50/80 text-slate-400",
  unknown: "border-rose-200/80 bg-rose-50/90 text-rose-700",
};
const MISMATCH_BANNER_EMAIL_ALLOWLIST = new Set(["cameronjordanp@gmail.com"]);

export default function BuildStamp() {
  const { user } = useAuth();
  const [databaseMigration, setDatabaseMigration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadReleaseStatus() {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc("get_release_status");
      if (cancelled) return;

      if (rpcError) {
        setError(rpcError);
        setDatabaseMigration(null);
      } else {
        setDatabaseMigration(data?.database_migration ?? null);
      }
      setLoading(false);
    }

    loadReleaseStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  const syncState = useMemo(() => getReleaseSyncState({
    expectedMigration: RELEASE_INFO.expectedMigration,
    databaseMigration,
    loading,
    error,
  }), [databaseMigration, loading, error]);
  const canSeeMismatchBanner = MISMATCH_BANNER_EMAIL_ALLOWLIST.has(normalizeEmail(user?.email));
  const showMismatchBanner = canSeeMismatchBanner && !loading && syncState.tone !== "synced";

  return (
    <>
      {showMismatchBanner ? (
        <div
          data-testid="build-mismatch-banner"
          className={[
            "pointer-events-none fixed right-3 top-7 z-[2147483647] w-[min(420px,calc(100vw-24px))] select-none rounded-lg border px-3 py-2 text-left shadow-lg backdrop-blur-md",
            TONE_CLASSES[syncState.tone] || TONE_CLASSES.unknown,
          ].join(" ")}
        >
          <div className="text-xs font-black uppercase tracking-[0.16em]">App / database mismatch</div>
          <div className="mt-1 font-mono text-[11px] font-semibold leading-5 tracking-[0.06em]">
            <span>{RELEASE_INFO.appStamp}</span>
            <span className="mx-2 opacity-50">|</span>
            <span>expected DB {formatDb(RELEASE_INFO.expectedMigration)}</span>
            <span className="mx-2 opacity-50">|</span>
            <span>actual {syncState.label}</span>
          </div>
        </div>
      ) : null}
      <div
        data-testid="build-stamp"
        title={`${RELEASE_INFO.appStamp}. ${syncState.title}`}
        className={[
          "pointer-events-none fixed bottom-2 right-2 z-[2147483646] select-none rounded border px-1.5 py-0.5 font-mono text-[9px] font-semibold leading-none tracking-[0.08em] shadow-sm backdrop-blur-sm",
          TONE_CLASSES[syncState.tone] || TONE_CLASSES.unknown,
        ].join(" ")}
      >
        <span>{RELEASE_INFO.appStamp}</span>
        <span className="mx-1 text-current opacity-45">|</span>
        <span>{syncState.label}</span>
      </div>
    </>
  );
}

function formatDb(version) {
  return version ? String(version).slice(-6) : "unknown";
}

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}
